import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getFirstAccessiblePath } from "../utils/permissions";
import {
  FaEye,
  FaEyeSlash,
  FaUserTie,
  FaEnvelope,
  FaLock,
} from "react-icons/fa";
import { HiOutlineUserGroup } from "react-icons/hi";
import logo from "../Images/Subtract.png";

const StaffLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("https://api.editezy.com/api/staff/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error("Staff Login - Failed to parse response JSON:", parseErr);
        setError("Received an invalid response from the server. Please try again.");
        return;
      }

      console.log("Staff Login Response:", data);

      if (!response.ok) {
        setError(data?.message || data?.error || "Invalid email or password.");
        return;
      }

      if (data?.success === false) {
        setError(data?.message || data?.error || "Invalid email or password.");
        return;
      }

      const staff = data?.staff || data?.data?.staff || data?.data || null;

      if (!staff) {
        setError("Staff information not received from server.");
        return;
      }

      const token = data?.token || data?.data?.token || staff?.token || null;

      // Save staff data to localStorage
      if (staff?._id) {
        localStorage.setItem("staffId", staff._id);
      } else if (staff?.id) {
        localStorage.setItem("staffId", staff.id);
      }

      if (staff?.name) {
        localStorage.setItem("staffName", staff.name);
      }

      if (staff?.email) {
        localStorage.setItem("staffEmail", staff.email);
      }

      if (staff?.phone) {
        localStorage.setItem("staffPhone", staff.phone);
      }

      if (typeof staff?.isActive !== "undefined") {
        localStorage.setItem("staffIsActive", JSON.stringify(staff.isActive));
      }

      if (token) {
        localStorage.setItem("staffToken", token);
      }

      // Save permissions if returned by the API
      if (staff?.permissions) {
        localStorage.setItem(
          "staffPermissions",
          JSON.stringify(staff.permissions)
        );
      }

      // Staff must land on an assigned module, never the admin dashboard.
      navigate(getFirstAccessiblePath() || "/staff");
    } catch (err) {
      console.error("Staff Login Error:", err);
      setError("Unable to connect to server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-cover bg-center"
      style={{
        backgroundImage:
          "url('https://img.freepik.com/free-photo/hand-painted-watercolor-background-with-sky-clouds-shape_24972-1095.jpg?t=st=1746429807~exp=1746433407~hmac=e3434110c0769d2ad42bd54e0534379335887da5831a723df88f4f74891e28d2&w=1380')",
      }}
    >
      <div className="bg-white/20 backdrop-blur-xl border border-white/30 shadow-2xl rounded-2xl w-full max-w-md overflow-hidden transform transition-all duration-300 hover:scale-[1.02]">
        <div className="p-8 sm:p-10 space-y-6 bg-white/40 backdrop-blur-sm">
          {/* Logo with Staff Badge */}
          <div className="flex justify-center relative">
            <div className="relative">
              <img
                src={logo}
                alt="EDITEZY Logo"
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>

          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              EDITEZY
            </h1>
            <p className="text-gray-700 text-sm mt-1 font-medium flex items-center justify-center gap-2">
              <HiOutlineUserGroup className="text-blue-500" />
              Staff Login
            </p>
          </div>

          {/* Decorative Line */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
              Access
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
          </div>

          {error && (
            <div className="p-3 text-red-700 bg-red-100/80 rounded-md shadow-sm text-sm flex items-center gap-2">
              <span className="text-red-500">⚠</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                className="block text-sm font-medium text-gray-800"
                htmlFor="staff-email"
              >
                <span className="flex items-center gap-2">
                  <FaEnvelope className="text-blue-500 text-xs" />
                  Email Address
                </span>
              </label>
              <div className="relative mt-1">
                <input
                  type="email"
                  id="staff-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="staff@editezy.com"
                  className="w-full px-4 py-3 pl-10 text-sm bg-white/50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 backdrop-blur-sm transition-all duration-200"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                className="block text-sm font-medium text-gray-800"
                htmlFor="staff-password"
              >
                <span className="flex items-center gap-2">
                  <FaLock className="text-purple-500 text-xs" />
                  Password
                </span>
              </label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  id="staff-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pl-10 pr-10 text-sm bg-white/50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 backdrop-blur-sm transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <FaEyeSlash size={18} />
                  ) : (
                    <FaEye size={18} />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3.5 px-6 text-white text-sm font-medium rounded-lg transition-all duration-300 relative overflow-hidden group ${
                isLoading
                  ? "bg-gradient-to-r from-blue-400 to-purple-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg hover:shadow-purple-500/30 hover:scale-[1.02]"
              }`}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Logging in...
                  </>
                ) : (
                  <>
                    <FaUserTie />
                    Staff Login
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Navigation to Admin Login */}
          <div className="text-center pt-2 border-t border-gray-300/50">
            <p className="text-gray-700 text-sm">
              Are you an admin?{" "}
              <Link
                to="/"
                className="font-semibold text-purple-600 hover:text-blue-600 transition-colors duration-200 hover:underline"
              >
                Admin Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLoginPage;